import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  MenuItem,
  Box,
  Switch,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import {
  Policy,
  PolicyWrite,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
} from '../../store/api/githubApi';
import { SideDrawer } from '../common/SideDrawer';

const RULE_KINDS: { value: PolicyWrite['rule_kind']; label: string }[] = [
  { value: 'max_severity_count', label: 'Max severity count — block on N+ issues at a severity' },
  { value: 'max_age_days', label: 'Max age — block on issues open past a deadline' },
  { value: 'min_category_score', label: 'Min category score — block below a score threshold' },
];
const CATEGORIES = ['security', 'complexity', 'dependencies', 'documentation'];
const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const ACTIONS: { value: string; label: string }[] = [
  { value: 'block', label: 'Block — fail the PR gate' },
  { value: 'flag', label: 'Flag — record only, does not fail the gate' },
];

interface PolicyFormDialogProps {
  open: boolean;
  onClose: () => void;
  repositoryId: number | null; // null = account-wide override
  editingPolicy?: Policy | null; // when set, edits in place instead of creating
}

const defaultParams = (ruleKind: string): Record<string, number | string> => {
  if (ruleKind === 'max_severity_count') return { min_severity: 'high', max_count: 0 };
  if (ruleKind === 'max_age_days') return { min_severity: 'high', max_days: 7 };
  return { min_score: 70 };
};

export const PolicyFormDialog: React.FC<PolicyFormDialogProps> = ({
  open,
  onClose,
  repositoryId,
  editingPolicy,
}) => {
  const [ruleKind, setRuleKind] = useState<string>('max_severity_count');
  const [category, setCategory] = useState<string>('security');
  const [action, setAction] = useState<string>('block');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [params, setParams] = useState<Record<string, number | string>>(defaultParams('max_severity_count'));

  const [createPolicy, { isLoading: isCreating, error: createError }] = useCreatePolicyMutation();
  const [updatePolicy, { isLoading: isUpdating, error: updateError }] = useUpdatePolicyMutation();
  const isSaving = isCreating || isUpdating;
  const error = createError || updateError;

  useEffect(() => {
    if (!open) return;
    if (editingPolicy) {
      setRuleKind(editingPolicy.rule_kind);
      setCategory(editingPolicy.category);
      setAction(editingPolicy.action);
      setEnabled(editingPolicy.enabled);
      setParams(editingPolicy.params);
    } else {
      setRuleKind('max_severity_count');
      setCategory('security');
      setAction('block');
      setEnabled(true);
      setParams(defaultParams('max_severity_count'));
    }
  }, [open, editingPolicy]);

  const handleRuleKindChange = (value: string) => {
    setRuleKind(value);
    setParams(defaultParams(value));
  };

  const handleSubmit = async () => {
    const payload: PolicyWrite = {
      repository_id: repositoryId,
      rule_kind: ruleKind,
      category,
      params,
      action,
      enabled,
    };
    if (editingPolicy) {
      await updatePolicy({ policy_id: editingPolicy.id, ...payload }).unwrap();
    } else {
      await createPolicy(payload).unwrap();
    }
    onClose();
  };

  const title = editingPolicy ? 'Edit Policy' : 'New Policy';
  const description = editingPolicy
    ? 'Update configuration for this policy rule'
    : (repositoryId
        ? 'Create a policy for this repository'
        : 'Create an account-wide policy override');

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width={{ xs: '100vw', sm: 500, md: 540 }}
      footer={
        <>
          <Button
            onClick={onClose}
            disabled={isSaving}
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'none',
              px: 2.5,
              borderRadius: '10px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving}
            sx={{
              fontWeight: 800,
              textTransform: 'none',
              px: 3,
              borderRadius: '10px',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
            }}
          >
            {isSaving ? 'Saving...' : editingPolicy ? 'Save Changes' : 'Create Policy'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!!error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {'data' in error && (error.data as { detail?: string })?.detail
              ? (error.data as { detail: string }).detail
              : 'Failed to save policy.'}
          </Alert>
        )}

        {/* RULE */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#64748b',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1,
            }}
          >
            RULE
          </Typography>
          <TextField
            select
            value={ruleKind}
            onChange={(e) => handleRuleKindChange(e.target.value)}
            disabled={!!editingPolicy}
            helperText={editingPolicy ? 'Rule type cannot be changed — delete and recreate instead.' : undefined}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#ffffff',
              },
            }}
          >
            {RULE_KINDS.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* CATEGORY */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#64748b',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1,
            }}
          >
            CATEGORY
          </Typography>
          <TextField
            select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={!!editingPolicy}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#ffffff',
              },
            }}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* RULE PARAMETERS */}
        {(ruleKind === 'max_severity_count' || ruleKind === 'max_age_days') && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: '#64748b',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1,
                }}
              >
                MINIMUM SEVERITY
              </Typography>
              <TextField
                select
                value={params.min_severity ?? 'high'}
                onChange={(e) => setParams({ ...params, min_severity: e.target.value })}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                  },
                }}
              >
                {SEVERITIES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: '#64748b',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1,
                }}
              >
                {ruleKind === 'max_severity_count' ? 'MAX COUNT ALLOWED' : 'MAX AGE (DAYS)'}
              </Typography>
              {ruleKind === 'max_severity_count' ? (
                <TextField
                  type="number"
                  value={params.max_count ?? 0}
                  onChange={(e) => setParams({ ...params, max_count: Number(e.target.value) })}
                  inputProps={{ min: 0 }}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: '#ffffff',
                    },
                  }}
                />
              ) : (
                <TextField
                  type="number"
                  value={params.max_days ?? 7}
                  onChange={(e) => setParams({ ...params, max_days: Number(e.target.value) })}
                  inputProps={{ min: 0 }}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: '#ffffff',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {ruleKind === 'min_category_score' && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: '#64748b',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1,
              }}
            >
              MINIMUM SCORE (0–100)
            </Typography>
            <TextField
              type="number"
              value={params.min_score ?? 70}
              onChange={(e) => setParams({ ...params, min_score: Number(e.target.value) })}
              inputProps={{ min: 0, max: 100 }}
              helperText="Not yet enforced outside the PR gate — scan-time evaluation is planned but not built."
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#ffffff',
                },
              }}
            />
          </Box>
        )}

        {/* ACTION */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#64748b',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1,
            }}
          >
            ACTION
          </Typography>
          <TextField
            select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#ffffff',
              },
            }}
          >
            {ACTIONS.map((a) => (
              <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.2)' }} />

        {/* STATUS */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#64748b',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 1.5,
            }}
          >
            STATUS
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: '14px',
              bgcolor: 'rgba(248, 250, 252, 0.8)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={700} color="#0f172a">
                Policy Enabled
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {enabled ? 'Active and evaluated during automated PR checks' : 'Suspended — will not block or flag pull requests'}
              </Typography>
            </Box>
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="primary"
            />
          </Box>
        </Box>
      </Box>
    </SideDrawer>
  );
};

export default PolicyFormDialog;
